import React, { ChangeEvent, useRef, useState } from 'react';
import { ModalHeader, ModalBody, FormGroup, Row, Col, Button, Input, InputGroup, InputGroupAddon } from 'reactstrap';
import createNotification from '../../../../../utils/createNotification';
import { Field, Form } from '@availity/form';
import * as yup from 'yup';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import Rodal from '../../../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import '../../../../../components/SelectFileInputAvaility/SelectFileInputAvaility.scss';
import './AddBuyerNdaTemplateModal.scss';

interface NdaFileInputProps {
    inputId: string;
    attachedFile: File | null;
    onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    acceptedFileTypes: string;
}

const NdaFileInput = (props: NdaFileInputProps) => {
    const downloadFile = () => {
        if (props.attachedFile) {
            const blob = new Blob([props.attachedFile], { type: props.attachedFile.type });
            const objectURL = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectURL;
            link.download = props.attachedFile.name;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    return (
        <div className="select-file-input-availity">
            <div className="select-file">
                <label className="link-button" htmlFor={props.inputId}>
                    Select File
                </label>
                <input
                    hidden={true}
                    type="file"
                    id={props.inputId}
                    className="upload-file-input"
                    onChange={props.onFileChange}
                    accept={props.acceptedFileTypes}
                />
            </div>
            {props.attachedFile != null && (
                <InputGroup>
                    <Input
                        type="text"
                        disabled={true}
                        className="form-control file-display"
                        value={props.attachedFile.name}
                    />
                    <InputGroupAddon addonType="append">
                        <div className="input-group-text file-addon no-right-padding">
                            <FontAwesomeIcon
                                className="download-icon"
                                icon={faDownload}
                                onClick={downloadFile}
                            />
                        </div>
                    </InputGroupAddon>
                </InputGroup>
            )}
        </div>
    );
};

interface AddBuyerNdaTemplateModalProps {
    isOpened: boolean;
    onClose: () => void;
    afterSuccessfulAdd: () => void;
}

interface FormFields {
    name: string;
    sourceFile: string;
    redlineFile?: string;
}

export const AddBuyerNdaTemplateModal = (props: AddBuyerNdaTemplateModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const modalForm = useRef(null);
    const [attachedFile, setAttachedFile] = useState<File>(null);
    const [redlineFile, setRedlineFile] = useState<File>(null);
    const [invalidatePriorSignatures, setInvalidatePriorSignatures] = useState<boolean>(true);

    const saveTemplate = (fields: FormFields) => {
        if (!attachedFile) {
            createNotification('Please select a file to upload.', 'error');
            return;
        }

        setIsSaving(true);

        const formData = new FormData();
        formData.append('file', attachedFile);
        formData.append('name', fields.name);
        if (redlineFile) {
            formData.append('redlineFile', redlineFile);
        }
        formData.append('invalidatePriorSignatures', String(invalidatePriorSignatures));

        axios
            .post('/api/Marketplace/MarketplaceBuyersNdaTemplates', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then((response: any) => {
                if (response.status === 200) {
                    createNotification(
                        'Buyer NDA Template has been successfully uploaded.',
                        'success'
                    );
                    props.afterSuccessfulAdd();
                } else {
                    createNotification(
                        response.response?.data?.detail ??
                            'Unexpected error occurred while uploading template.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Unexpected error occurred while uploading template.',
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const changeFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = event.target.files[0];
        if (newFile) {
            setAttachedFile(newFile);
            modalForm.current.setFieldValue('sourceFile', newFile.name, true);
        }
    };

    const changeRedlineFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = event.target.files[0];
        if (newFile) {
            setRedlineFile(newFile);
            modalForm.current.setFieldValue('redlineFile', newFile.name, true);
        }
    };

    const renderForm = () => {
        return (
            <div>
                <Form
                    initialValues={{
                        name: '',
                        sourceFile: '',
                        redlineFile: '',
                    }}
                    onSubmit={(fields) => saveTemplate(fields)}
                    validationSchema={yup.object().shape({
                        sourceFile: yup.string().required('File is required'),
                        name: yup.string().required('Template name is required'),
                        redlineFile: yup.string(),
                    })}
                    innerRef={modalForm}>
                    <FormGroup className="template-inputs-form-group">
                        <Row>
                            <Col md={12}>
                                <label>NDA Document</label>
                                <NdaFileInput
                                    inputId="upload-nda-file"
                                    attachedFile={attachedFile}
                                    onFileChange={changeFileHandler}
                                    acceptedFileTypes=".doc,.docx"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <label>Template Name</label>
                                <Field
                                    name="name"
                                    type="text"
                                    className="form-control"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <label>Redline Document (optional)</label>
                                <NdaFileInput
                                    inputId="upload-redline-file"
                                    attachedFile={redlineFile}
                                    onFileChange={changeRedlineFileHandler}
                                    acceptedFileTypes=".pdf"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <div className="form-check">
                                    <Input
                                        type="checkbox"
                                        id="invalidate-prior-signatures"
                                        checked={invalidatePriorSignatures}
                                        onChange={(e) => setInvalidatePriorSignatures(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="invalidate-prior-signatures">
                                        This NDA update applies to all buyers, not only the ones who have not yet signed an NDA
                                    </label>
                                </div>
                            </Col>
                        </Row>
                    </FormGroup>
                </Form>
                <div className="footer">
                    <Button
                        className="cancel btn btn-secondary"
                        onClick={() => props.onClose()}>
                        Cancel
                    </Button>
                    <ButtonLoader
                        type="submit"
                        buttonText={'Upload'}
                        disabled={false}
                        isLoading={isSaving}
                        onClick={() => {
                            modalForm.current.handleSubmit();
                        }}
                        className={'btn btn-primary loader-submit'}
                        loaderButtonText={''}></ButtonLoader>
                </div>
            </div>
        );
    };

    return props.isOpened ? (
        <div className="add-buyer-nda-template-modal">
            <Rodal
                width={500}
                height={450}
                visible={props.isOpened}
                animation={'rotate'}
                onClose={() => props.onClose()}
                showMask={false}>
                <ModalHeader>Upload Buyer NDA Template</ModalHeader>
                <ModalBody>{renderForm()}</ModalBody>
            </Rodal>
        </div>
    ) : (
        <></>
    );
};

